import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardBody, CardHeader, Table, Row, Col, Input, Button, Modal, ModalHeader, ModalBody, Badge } from 'reactstrap';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CommentIcon from '@mui/icons-material/Comment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LinearProgress from '@mui/material/LinearProgress';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useHistory } from 'react-router-dom';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { OPERATIONAL_STATUS_SECURITY_HISTORY_LIST } from 'src/utils/pnd/PndConstants';
// ─── Image Lightbox ───────────────────────────────────────────────────────────
const ImageLightbox = ({ images, startIndex, onClose }) => {
    const [current, setCurrent] = React.useState(startIndex);
    React.useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight') setCurrent(c => Math.min(c + 1, images.length - 1));
            if (e.key === 'ArrowLeft') setCurrent(c => Math.max(c - 1, 0));
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [images.length, onClose]);

    return (
        <div
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', color: '#fff', fontSize: '2rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            {current > 0 && (
                <button onClick={(e) => { e.stopPropagation(); setCurrent(c => c - 1); }} style={{ position: 'absolute', left: 16, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: '2rem', borderRadius: '50%', width: 48, height: 48, cursor: 'pointer' }}>‹</button>
            )}
            <img
                src={images[current]}
                alt={`Proof ${current + 1}`}
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: '90vw', maxHeight: '88vh', borderRadius: 8, objectFit: 'contain', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
            />
            {current < images.length - 1 && (
                <button onClick={(e) => { e.stopPropagation(); setCurrent(c => c + 1); }} style={{ position: 'absolute', right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: '2rem', borderRadius: '50%', width: 48, height: 48, cursor: 'pointer' }}>›</button>
            )}
            <div style={{ position: 'absolute', bottom: 20, color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 600, letterSpacing: 1 }}>
                {current + 1} / {images.length}
            </div>
        </div>
    );
};

// ─── Single Decline Entry Card (on-demand image fetch) ────────────────────────
const DeclineEntry = ({ detail, idx, orderId }) => {
    const [images, setImages] = React.useState([]);
    const [loadingImgs, setLoadingImgs] = React.useState(false);
    const [lightbox, setLightbox] = React.useState(null);
    const [fetched, setFetched] = React.useState(false);   // has the API been called?
    const [showImgs, setShowImgs] = React.useState(false); // toggle panel visibility

    const fetchImages = () => {
        if (fetched) {
            // Already fetched — just toggle visibility
            setShowImgs(v => !v);
            return;
        }
        const declinedId = detail?.declinedId;
        setLoadingImgs(true);
        PndRestApi.getDeclineAttachments(orderId, declinedId)
            .then(res => {
                if (res?.data?.status === 200 && Array.isArray(res.data.data)) {
                    const urls = res.data.data.map(item =>
                        typeof item === 'string' ? item : (item.imageUrl || item.filePath || item.url || item.imagePath || '')
                    ).filter(Boolean);
                    setImages(urls);
                }
            })
            .catch(() => { })
            .finally(() => {
                setFetched(true);
                setLoadingImgs(false);
                setShowImgs(true);
            });
    };

    return (
        <>
            <div className="bg-white rounded-3 shadow-sm border-start border-4 border-danger" style={{ padding: '16px', borderTop: '1px solid #eee', borderRight: '1px solid #eee', borderBottom: '1px solid #eee' }}>
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <span style={{ background: '#dc3545', color: 'white', fontSize: '0.7rem', fontWeight: 800, padding: '2px 10px', borderRadius: '10px' }}>#{idx + 1}</span>
                    {detail.declinedDateTime && (
                        <div className="d-flex align-items-center gap-1 text-muted">
                            <AccessTimeIcon style={{ fontSize: '0.9rem' }} />
                            <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{new Date(detail.declinedDateTime).toLocaleString()}</span>
                        </div>
                    )}
                </div>

                {/* Declined By */}
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <div className="d-flex align-items-center gap-2">
                        <PersonIcon style={{ color: '#888', fontSize: '1rem' }} />
                        <span className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Declined By</span>
                    </div>
                    <span className="fw-bold" style={{ fontSize: '0.85rem' }}>{detail.declinedBy || '—'}</span>
                </div>

                {/* Reason */}
                <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <div className="d-flex align-items-center gap-2">
                        <WarningAmberIcon style={{ color: '#dc3545', fontSize: '1rem' }} />
                        <span className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Reason</span>
                    </div>
                    <span className="fw-bold text-danger text-end" style={{ fontSize: '0.85rem', maxWidth: '60%' }}>{detail.declinedReason || '—'}</span>
                </div>

                {/* Remarks */}
                {detail.remarks && (
                    <div className="pt-2 pb-2 border-bottom">
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <CommentIcon style={{ color: '#888', fontSize: '1rem' }} />
                            <span className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Remarks</span>
                        </div>
                        <div className="bg-light p-2 rounded ps-3 border-start border-2" style={{ fontSize: '0.85rem', color: '#555', fontStyle: 'italic' }}>
                            {detail.remarks}
                        </div>
                    </div>
                )}

                {/* View Proof Images Button */}
                <div className="pt-2">
                    <button
                        onClick={fetchImages}
                        disabled={loadingImgs}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: showImgs ? '#f8d7da' : '#fff5f5',
                            border: '1.5px solid #f1aeb5',
                            borderRadius: 20,
                            padding: '4px 14px',
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            color: '#dc3545',
                            cursor: loadingImgs ? 'wait' : 'pointer',
                            transition: 'all 0.2s',
                        }}
                        onMouseEnter={e => !loadingImgs && (e.currentTarget.style.background = '#f8d7da')}
                        onMouseLeave={e => !loadingImgs && (e.currentTarget.style.background = showImgs ? '#f8d7da' : '#fff5f5')}
                    >
                        {loadingImgs ? (
                            <>⏳ Loading...</>
                        ) : showImgs ? (
                            <>🖼 Hide Proof Images {images.length > 0 && `(${images.length})`}</>
                        ) : (
                            <>📎 View Proof Images</>
                        )}
                    </button>

                    {/* Image thumbnails panel */}
                    {showImgs && (
                        <div className="mt-2">
                            {images.length === 0 ? (
                                <span className="text-muted" style={{ fontSize: '0.78rem', fontStyle: 'italic' }}>
                                    No proof images uploaded for this decline.
                                </span>
                            ) : (
                                <div className="d-flex flex-wrap gap-2">
                                    {images.map((url, i) => (
                                        <div
                                            key={i}
                                            onClick={() => setLightbox(i)}
                                            style={{ width: 72, height: 72, borderRadius: 8, overflow: 'hidden', border: '2px solid #dee2e6', cursor: 'pointer', transition: 'transform 0.15s, border-color 0.15s', flexShrink: 0 }}
                                            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.borderColor = '#dc3545'; }}
                                            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.borderColor = '#dee2e6'; }}
                                        >
                                            <img src={url} alt={`Proof ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.parentNode.style.display = 'none'; }} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {lightbox !== null && (
                <ImageLightbox images={images} startIndex={lightbox} onClose={() => setLightbox(null)} />
            )}
        </>
    );
};


// ─── Decline History Modal ────────────────────────────────────────────────────
const DeclineHistoryModal = ({ isOpen, toggle, order }) => {
    if (!order) return null;
    const details = order.declinedDetails || [];
    const orderId = order.orderId ?? order.id;

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable className="modal-dialog-scrollable">
            <ModalHeader toggle={toggle} style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #EE5253 100%)', color: 'white' }}>
                <div className="d-flex align-items-center gap-2">
                    <WarningAmberIcon style={{ fontSize: '1.2rem' }} />
                    <span className="fw-bold">Decline History — {order.orderNo}</span>
                </div>
            </ModalHeader>
            <ModalBody style={{ background: '#f8f9fa', padding: '15px' }}>
                <div className="d-flex justify-content-between align-items-center mb-3 px-2">
                    <span className="text-muted small fw-bold text-uppercase">Timeline</span>
                    <Badge color="danger" pill className="px-3">
                        {details.length} {details.length === 1 ? 'Decline' : 'Declines'}
                    </Badge>
                </div>
                {details.length === 0 ? (
                    <div className="text-center py-5 bg-white rounded-3 shadow-sm border">
                        <SentimentDissatisfiedIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                        <p className="text-muted mb-0">No records found.</p>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {details.map((detail, idx) => (
                            <DeclineEntry
                                key={detail.orderDeclinedId ?? detail.id ?? idx}
                                detail={detail}
                                idx={idx}
                                orderId={orderId}
                            />
                        ))}
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SecurityHistory = () => {
    const history = useHistory();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    // Pagination state — driven by server response
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const fetchHistory = useCallback(async (page = 0) => {
        setLoading(true);
        try {
            const response = await PndRestApi.GetSecurityOrderList({ operationalStatus: OPERATIONAL_STATUS_SECURITY_HISTORY_LIST, page, size: pageSize });
            if (response && response.data && response.data.status === 200) {
                const paged = response.data.data || {};
                const content = paged.content || [];
                // Exclude pending (null) — history shows processed orders only
                const processed = content.filter(r => r.securityApprovalStatus !== null);
                setData(processed);
                setCurrentPage(paged.currentPage ?? page);
                setTotalElements(paged.totalElements ?? 0);
                setTotalPages(paged.totalPages ?? 1);
            }
        } catch (error) {
            console.error('Error fetching security history:', error);
        } finally {
            setLoading(false);
        }
    }, [pageSize]);

    useEffect(() => {
        fetchHistory(0);
    }, [fetchHistory]);

    const handlePageChange = (newPage) => {
        if (newPage < 0 || newPage >= totalPages) return;
        fetchHistory(newPage);
    };

    // Client-side filter + search within the current page
    const filteredData = useMemo(() => {
        let d = data;
        if (activeFilter === 'approved') d = data.filter(r => r.securityApprovalStatus === 1);
        if (activeFilter === 'declined') d = data.filter(r => r.securityApprovalStatus === 2);
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            d = d.filter(r =>
                (r.orderNo || '').toLowerCase().includes(term) ||
                (r.customerName || '').toLowerCase().includes(term) ||
                (r.riderName || '').toLowerCase().includes(term)
            );
        }
        return d;
    }, [data, activeFilter, searchTerm]);

    const tabs = [
        { key: 'all', label: 'All', color: '#007bff' },
        { key: 'approved', label: 'Approved', color: '#28a745' },
        { key: 'declined', label: 'Declined', color: '#dc3545' },
    ];

    const openDeclineModal = (item) => {
        setSelectedOrder(item);
        setModalOpen(true);
    };

    const handleViewDetails = (item) => {
        history.push({
            pathname: '/web_app/order/approval/details',
            state: { order: item, fromHistory: true }
        });
    };

    const startRow = currentPage * pageSize + 1;
    const endRow = Math.min(startRow + pageSize - 1, totalElements);

    return (
        <Card className="border-0 shadow-sm">
            <CardHeader className="py-3 bg-white border-bottom d-flex align-items-center justify-content-between">
                <h2 className="mb-0 fw-bold">Security History</h2>
                <span className="text-muted small">Total: <strong>{totalElements}</strong> records</span>
            </CardHeader>
            <CardBody>
                {loading && <LinearProgress className="mb-2" />}

                {/* Filter Tabs + Search */}
                <Row className="g-2 mb-3 align-items-center">
                    <Col xs={12} md={6}>
                        <div className="d-flex gap-2 flex-wrap">
                            {tabs.map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveFilter(tab.key)}
                                    style={{
                                        padding: '4px 14px',
                                        borderRadius: '20px',
                                        border: `2px solid ${tab.color}`,
                                        fontSize: '0.78rem',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        background: activeFilter === tab.key ? tab.color : '#fff',
                                        color: activeFilter === tab.key ? '#fff' : tab.color,
                                        transition: 'all 0.2s'
                                    }}
                                >{tab.label}</button>
                            ))}
                        </div>
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="input-group input-group-sm">
                            <span className="input-group-text bg-white border-end-0">
                                <SearchIcon fontSize="small" className="text-muted" />
                            </span>
                            <Input
                                type="text"
                                placeholder="Search by Order No, Customer, Rider..."
                                className="border-start-0"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <Button size="sm" color="light" onClick={() => setSearchTerm('')}>✕</Button>
                            )}
                        </div>
                    </Col>
                </Row>

                {/* Table */}
                {!loading && filteredData.length === 0 ? (
                    <div className="text-center py-5">
                        <SentimentDissatisfiedIcon sx={{ fontSize: 50, color: '#ccc' }} />
                        <p className="text-muted mb-0 mt-1">No records found.</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table hover className="align-middle table--security-approval table--security-history mb-0">
                            <thead>
                                <tr style={{ background: '#f8f9fa' }}>
                                    <th style={{ width: '60px' }}>#</th>
                                    <th className="text-center">Action</th>
                                    <th>Order No</th>
                                    <th>ERP No</th>
                                    <th>Customer</th>
                                    <th>Order Status</th>
                                    <th>Rider</th>
                                    <th className="text-center">Approval Status</th>
                                    <th className="text-center">Decline Count</th>

                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.map((item, index) => (
                                    <tr key={item.orderId || index}>
                                        <td className="text-center">{currentPage * pageSize + index + 1}</td>
                                        <td className="text-center">
                                            <Tooltip title="View Details" arrow>
                                                <IconButton size="small" onClick={() => handleViewDetails(item)} style={{ color: '#007bff' }}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </td>
                                        <td className="fw-bold text-primary">{item.orderNo || '—'}</td>
                                        <td className="fw-bold text-primary">{item.erpNo || '—'}</td>
                                        <td>
                                            <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{item.customerName || '—'}</div>
                                            <small className="text-muted">{item.customerCode}</small>
                                        </td>
                                        <td style={{ fontSize: '0.85rem' }}>{item.orderStatus || '—'}</td>
                                        <td style={{ fontSize: '0.85rem' }}>{item.riderName || '—'}</td>
                                        <td className="text-center">
                                            {item.securityApprovalStatus === 1 ? (
                                                <span style={{ background: '#d4edda', color: '#155724', padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700 }}>
                                                    <CheckCircleIcon style={{ fontSize: '0.85rem', verticalAlign: 'middle', marginRight: '3px' }} />Approved
                                                </span>
                                            ) : (
                                                <span style={{ background: '#f8d7da', color: '#721c24', padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700 }}>
                                                    <CancelIcon style={{ fontSize: '0.85rem', verticalAlign: 'middle', marginRight: '3px' }} />Declined
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            {item.declinedDetails?.length > 0 ? (
                                                <button
                                                    onClick={() => openDeclineModal(item)}
                                                    style={{
                                                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '20px',
                                                        padding: '3px 12px',
                                                        fontSize: '0.78rem',
                                                        fontWeight: 700,
                                                        cursor: 'pointer',
                                                        transition: 'transform 0.15s',
                                                        boxShadow: '0 2px 6px rgba(220,53,69,0.3)'
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
                                                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                                >
                                                    {item.declinedDetails.length} {item.declinedDetails.length === 1 ? 'Decline' : 'Declines'} ↗
                                                </button>
                                            ) : (
                                                <span className="text-muted">—</span>
                                            )}
                                        </td>
                                        {/* <td style={{ fontSize: '0.8rem', color: '#666' }}>
                                            {item.goodsReceivedDateTime
                                                ? new Date(item.goodsReceivedDateTime).toLocaleString()
                                                : '—'}
                                        </td> */}

                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 0 && (
                    <div className="d-flex align-items-center justify-content-between mt-3 px-1">
                        <span className="text-muted small">
                            Showing <strong>{startRow}</strong>–<strong>{endRow}</strong> of <strong>{totalElements}</strong> records
                        </span>
                        <div className="d-flex align-items-center gap-1">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 0}
                                style={{
                                    border: '1.5px solid #dee2e6',
                                    borderRadius: '8px',
                                    background: currentPage === 0 ? '#f8f9fa' : '#fff',
                                    color: currentPage === 0 ? '#aaa' : '#333',
                                    cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                                    padding: '4px 8px',
                                    display: 'flex', alignItems: 'center'
                                }}
                            >
                                <NavigateBeforeIcon fontSize="small" />
                            </button>

                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => handlePageChange(i)}
                                    style={{
                                        border: '1.5px solid',
                                        borderColor: currentPage === i ? '#667eea' : '#dee2e6',
                                        borderRadius: '8px',
                                        background: currentPage === i ? '#667eea' : '#fff',
                                        color: currentPage === i ? '#fff' : '#333',
                                        cursor: 'pointer',
                                        padding: '4px 10px',
                                        fontSize: '0.8rem',
                                        fontWeight: currentPage === i ? 700 : 400,
                                        minWidth: '34px'
                                    }}
                                >
                                    {i + 1}
                                </button>
                            ))}

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage >= totalPages - 1}
                                style={{
                                    border: '1.5px solid #dee2e6',
                                    borderRadius: '8px',
                                    background: currentPage >= totalPages - 1 ? '#f8f9fa' : '#fff',
                                    color: currentPage >= totalPages - 1 ? '#aaa' : '#333',
                                    cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                                    padding: '4px 8px',
                                    display: 'flex', alignItems: 'center'
                                }}
                            >
                                <NavigateNextIcon fontSize="small" />
                            </button>
                        </div>
                    </div>
                )}

            </CardBody>

            <DeclineHistoryModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(false)}
                order={selectedOrder}
            />
        </Card>
    );
};

export default SecurityHistory;
